<template>
  <a-button class="function-search-trigger" :class="{collapsed}" type="text" aria-label="全局功能搜索" aria-keyshortcuts="Alt+q Control+k Meta+k" title="搜索功能（Alt+Q / Ctrl+K，Mac：⌘+K）" @click="visible = true"><SearchOutlined /><span v-if="!collapsed">搜索功能</span></a-button>
  <a-modal v-model:visible="visible" title="搜索功能" :footer="null" :width="620" @after-close="query = ''">
    <a-input ref="input" v-model:value="query" allow-clear placeholder="输入功能名称或用途，如：订单、皮肤、客户" aria-label="搜索管理端功能" @keydown="onKey"><template #prefix><SearchOutlined /></template></a-input>
    <p class="search-hint">仅显示当前账号有权限的功能；点击结果直接进入。<span>{{ results.length }} 项</span></p>
    <div v-if="results.length" class="function-search-results" aria-label="功能搜索结果">
      <button v-for="(item,index) in results" :key="item.id" type="button" :class="{ selected: selected === index }" @click="open(item)">
        <strong>{{ item.title }}<span>进入 →</span></strong><small>{{ item.group }}</small><p v-if="item.description">{{ item.description }}</p>
      </button>
    </div>
    <a-empty v-else description="未找到可使用的功能，请更换关键词或联系管理员核对权限。" />
  </a-modal>
</template>
<script setup lang="ts">
import { dismissNavigationHoverHints, restoreNavigationHoverHints } from '@/utils/navigationHoverHints'
import {isSafeEnter} from '@/utils/enterActions'
import { locateFunctionTarget } from '@/utils/functionSearchFocus'
import { computed, nextTick, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { allOrdersSearchFocusRequested } from '@/utils/allOrdersSearchFocus'
import { isAllOrdersShortcut, isFunctionSearchShortcut } from '@/utils/functionSearchShortcut'
import { useRouter, useRoute } from 'vue-router'
import { SearchOutlined } from '@ant-design/icons-vue'
import store from '@/store'
import { buildFunctionSearch, searchFunctions, FunctionSearchItem } from '@/utils/adminFunctionSearch'
import { adminFunctionDescriptions } from '@/utils/adminFunctionDescriptions'
import { generateTitle } from '@/utils/lang'
import { administrationTitleKey } from '@/utils/platformAdministration'
import { canConfigureFunctionGuides } from '@/utils/functionGuides'
defineProps<{collapsed?:boolean}>()
const router = useRouter(), route = useRoute()
const visible = ref(false), query = ref(''), selected = ref(0), input = ref()
const canOpen = (path: string) => {
  const resolved = router.resolve(path), meta = resolved.meta, user = store.getters.userInfo
  return Boolean(resolved.matched.length && !resolved.matched.some(item => item.path.includes(':'))
    && (!meta.requiresPlatform || Number(user.orgId) === 1000)
    && (!meta.requiresPlatformSuperAdmin || Number(user.orgId) === 1000 && Number(user.roleId) === 1)
    && (!meta.requiresFunctionGuides || canConfigureFunctionGuides(user, store.getters.functions?.allFunc)))
}
const catalog = computed(() => store.getters.token ? buildFunctionSearch(store.getters.functions?.allFunc, store.getters.functions?.parentFunc, store.getters.userInfo,
  (key, fallback) => generateTitle(administrationTitleKey(key, store.getters.userInfo.orgId), fallback), adminFunctionDescriptions, canOpen) : [])
const results = computed(() => searchFunctions(catalog.value, query.value))
watch(results, async () => { selected.value = 0; await nextTick(); const list=document.querySelector('.function-search-results');if(list)list.scrollTop=0 })
watch(visible, async value => { if (value) { await nextTick(); input.value?.focus() } })
watch(() => [store.getters.token, store.getters.userInfo?.id, store.getters.userInfo?.orgId].join(':'), () => { visible.value = false; query.value = '' })
watch(() => route.fullPath, () => { visible.value = false })
const open = (item: FunctionSearchItem) => {
  if (!catalog.value.some(current => current.id === item.id && current.path === item.path) || !canOpen(item.path)) return
  visible.value = false; query.value = ''
  const destination=router.resolve(item.path);router.push({path:destination.path,query:{...destination.query,...(destination.path==='/products/bokes'?{productType:0}:{}),_find:String(Date.now())}})
}
let clearFocus:()=>void=()=>undefined
watch(()=>route.fullPath,async()=>{clearFocus();await nextTick();const focus=route.query.focus;if(typeof focus==='string' && focus.length<=100)clearFocus=locateFunctionTarget(focus)},{immediate:true})
const onSearchShortcut = async (event: KeyboardEvent) => {
  if (!store.getters.token) return
  if (isAllOrdersShortcut(event, true)) {
    event.preventDefault()
    event.stopPropagation()
    if (event.repeat) return
    dismissNavigationHoverHints()
    visible.value = false
    allOrdersSearchFocusRequested.value = true
    if (route.path !== '/orders/all') {
      try {
        const failure = await router.push('/orders/all')
        if (failure) allOrdersSearchFocusRequested.value = false
      } catch {
        allOrdersSearchFocusRequested.value = false
      }
    }
    return
  }
  if (!isFunctionSearchShortcut(event, true)) return
  event.preventDefault()
  event.stopPropagation()
  if (event.repeat) return
  dismissNavigationHoverHints()
  visible.value = true
  nextTick(() => input.value?.focus())
}
onMounted(() => {
  window.addEventListener('keydown', onSearchShortcut, true)
})
onBeforeUnmount(() => {
  clearFocus()
  restoreNavigationHoverHints()
  window.removeEventListener('keydown', onSearchShortcut, true)
})
const onKey = (event: KeyboardEvent) => {
  if(event.key==='Enter' && !isSafeEnter(event))return
  if (event.isComposing || !results.value.length) return
  if (event.key === 'Enter') { event.preventDefault(); open(results.value[selected.value]) }
  if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault(); selected.value = (selected.value + (event.key === 'ArrowDown' ? 1 : -1) + results.value.length) % results.value.length
    nextTick(() => document.querySelector('.function-search-results button.selected')?.scrollIntoView({ block: 'nearest' }))
  }
}
</script>
<style scoped>
.function-search-trigger{width:100%;border:1px solid var(--admin-skin-border,rgba(128,145,164,.35));background:rgba(128,145,164,.1);justify-content:flex-start}.function-search-trigger.collapsed{justify-content:center;padding:0}
.function-search-trigger:not(.collapsed){display:grid;grid-template-columns:16px minmax(0,1fr);column-gap:4px;row-gap:0;padding:3px 6px}
.function-search-trigger:not(.collapsed) :deep(.anticon){grid-row:1;grid-column:1}
.function-search-trigger:not(.collapsed)>span:not(.anticon){grid-column:2;line-height:14px;white-space:nowrap;text-align:left}
.function-search-trigger{display:inline-flex;align-items:center;gap:3px;flex-shrink:0;height:36px;padding:0 8px;font-size:14px;color:inherit;border-radius:6px}.function-search-trigger :deep(.anticon){font-size:22px;line-height:1;display:inline-flex;align-items:center}.function-search-trigger:hover{color:var(--activeColor,#1890ff);background:var(--admin-skin-soft,#edf3f9)}
.search-hint{display:flex;justify-content:space-between;gap:12px;color:#7c8998;font-size:12px;margin:12px 0}.search-hint span{white-space:nowrap}.function-search-results{max-height:min(55vh,480px);overflow-y:auto;display:grid;gap:7px}.function-search-results button{text-align:left;border:1px solid #edf0f4;background:transparent;border-radius:7px;padding:11px 14px;width:100%;cursor:pointer;color:inherit}.function-search-results button:hover,.function-search-results button:focus-visible,.function-search-results button.selected{background:var(--admin-skin-soft,#edf3f9);border-color:var(--activeColor,#1890ff)}.function-search-results strong{display:flex;justify-content:space-between;gap:12px;font-size:14px}.function-search-results strong span{font-size:12px;font-weight:400;color:var(--activeColor,#1890ff)}.function-search-results small{color:#7c8998}.function-search-results p{font-size:12px;color:#7c8998;margin:4px 0 0;overflow-wrap:anywhere}
</style>

<style>
.function-search-highlight{outline:2px solid var(--activeColor,#1890ff)!important;outline-offset:4px;border-radius:5px;box-shadow:0 0 0 6px var(--admin-skin-soft,#edf3f9)}
</style>
